/**
 * useUserImageTransform
 *
 * Hook personalizzato che fornisce gesture handlers ottimizzati per le trasformazioni UserImage.
 * Utilizza SharedValues e worklets per performance 60fps senza re-render.
 *
 * Integra con UserImageTransformContext per gestione centralizzata degli stati temporanei.
 * Usa valori null per indicare stato di quiete (nessuna trasformazione temporanea attiva).
 */
import { useCallback } from 'react';
import { useUserImageTransformContext } from '../contexts/UserImageTransformContext';
import { IUserImageModel } from '../store/_models/userImage';

/**
 * Props per il hook useUserImageTransform
 */
interface UseUserImageTransformProps {
  userImage: IUserImageModel;
}

/**
 * Hook per gestire le trasformazioni UserImage con performance ottimizzate
 */
export const useUserImageTransform = ({ userImage }: UseUserImageTransformProps) => {
  const context = useUserImageTransformContext();

  const {
    tempPanSV,
    tempZoomSV,
    tempRotationSV
  } = context;

  /**
   * Funzioni utili per i controlli UI (sliders)
   * Usano valori null per indicare stato di quiete
   */

  /**
   * Imposta zoom temporaneo (per slider)
   * @param zoomDelta - Delta di zoom, null per stato di quiete
   */
  const setTempZoom = useCallback((zoomDelta: number | null) => {
    tempZoomSV.value = zoomDelta;
  }, [tempZoomSV]);

  /**
   * Imposta rotazione temporanea (per slider)
   * @param rotationDelta - Delta di rotazione, null per stato di quiete
   */
  const setTempRotation = useCallback((rotationDelta: number | null) => {
    tempRotationSV.value = rotationDelta;
  }, [tempRotationSV]);

  /**
   * Imposta pan temporaneo (per controlli manuali)
   * @param panOffset - Offset di pan, null per stato di quiete
   */
  const setTempPan = useCallback((panOffset: { x: number; y: number } | null) => {
    tempPanSV.value = panOffset;
  }, [tempPanSV]);

  return {
    // SharedValues (per accesso diretto se necessario)
    // null indica stato di quiete (nessuna trasformazione temporanea attiva)
    tempPanSV,
    tempZoomSV,
    tempRotationSV,

    // Controlli per UI (accettano null per stato di quiete)
    setTempZoom,
    setTempRotation,
    setTempPan,
  };
};